import { PATHS } from "@/constants/paths";

import { toSafeSameOriginPath } from "./safe-redirect";

interface IRedirectToDocsProps {
  accessToken: string;
  returnPath?: string;
}

const RETURN_URL_PARAM = "returnUrl";

// NOTE: `path` is already a normalized same-origin path, so its pathname is
// everything before the query or hash.
const isDocsPath = (path: string): boolean => {
  const [pathname] = path.split(/[?#]/);

  return pathname === PATHS.DOCS || pathname.startsWith(`${PATHS.DOCS}/`);
};

// NOTE: NSAI-6750 — the docs page to land on after the docs_session handoff,
// read from `?returnUrl=`. Only same-origin paths under /docs are honored (it
// is an attacker-reachable query param); anything else yields null, so the
// sign-in page behaves as it does for every other visitor.
export const getDocsReturnPath = (search: string): string | null => {
  const candidate = new URLSearchParams(search).get(RETURN_URL_PARAM);
  const path = toSafeSameOriginPath({ candidate, fallback: "" });

  return path && isDocsPath(path) ? path : null;
};

// NOTE: NSAI-6750 — a new tab opened on a docs deep link (e.g. the global
// search "Search the documentation" action) goes through the sign-in handoff
// instead of straight to /docs: the docs_session cookie is a copy of an access
// token that may have expired since it was set, and the proxy's 401 bounce
// keeps only /docs, dropping the page and its query.
export const buildDocsHandoffPath = (docsPath: string): string =>
  `${PATHS.AUTH.SIGN_IN}?${new URLSearchParams({ [RETURN_URL_PARAM]: docsPath }).toString()}`;

// NOTE: Best-effort — even if the session POST fails, following through to
// /docs is no worse than before (forward_auth will just bounce back to
// sign-in again), so navigation always happens.
export const redirectToDocs = async ({
  accessToken,
  returnPath = PATHS.DOCS,
}: IRedirectToDocsProps): Promise<void> => {
  try {
    // NOTE: fetch only throws on network-level failures — a non-2xx
    // response (e.g. 401/500) resolves normally and isn't checked here.
    // That's intentional: Caddy's forward_auth re-validates the cookie on
    // the next /docs request and will redirect back to sign-in if it
    // didn't get set, so failing silently here is no worse than not
    // checking at all.
    await fetch("/api/docs/session", {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch {
    // NOTE: Best-effort; ignore docs session errors.
  } finally {
    globalThis.location.href = returnPath;
  }
};
